import PitchPlayer from '@components/pitch-player';
import type { PitchPlayerFixture } from '@components/pitch-player';
import { EmptySlot } from '@components/team-lineup/empty-slot';
import type {
  BuildPosition,
  ReviewLineup
} from '@lib/my-team/build-review-lineup';

const POSITION_ORDER = ['GK', 'DEF', 'MID', 'FWD'] as const;

// Match the standard pick view's "next 3 GWs" fixture strip.
const BUILD_FIXTURE_COUNT = 3;

export type SlotLocation = { position: BuildPosition; index: number };

type BuildLineupProps = {
  lineup: ReviewLineup;
  captainId: string | null;
  viceCaptainId: string | null;
  fixturesMap: Map<string, PitchPlayerFixture[]>;
  onEmptySlotPress: (location: SlotLocation) => void;
  onMakeCaptain: (playerId: string) => void;
  onMakeViceCaptain: (playerId: string) => void;
  onRemovePlayer: (playerId: string) => void;
};

type RenderSlot = {
  location: SlotLocation;
  isStarter: boolean;
};

/**
 * Builds the render order for one pitch row: starters first (left to right),
 * then bench entries for that position. The bench split is driven by the
 * lineup's formation - everything past the formation count is a bench slot.
 */
const rowSlots = (
  lineup: ReviewLineup,
  position: BuildPosition
): RenderSlot[] => {
  const startingCount = position === 'GK' ? 1 : lineup.formation[position];
  return lineup[position].map((_, index) => ({
    location: { position, index },
    isStarter: index < startingCount
  }));
};

const BuildLineup = ({
  lineup,
  captainId,
  viceCaptainId,
  fixturesMap,
  onEmptySlotPress,
  onMakeCaptain,
  onMakeViceCaptain,
  onRemovePlayer
}: BuildLineupProps) => {
  const renderSlot = ({ location, isStarter }: RenderSlot) => {
    const player = lineup[location.position][location.index];

    if (!player) {
      return (
        <EmptySlot
          position={location.position}
          onPress={() => onEmptySlotPress(location)}
        />
      );
    }

    return (
      <PitchPlayer
        name={player.displayName}
        teamShortName={player.team.shortName}
        kitImageUrl={player.kitImageUrl}
        price={player.price}
        fixtures={fixturesMap.get(player.id) ?? []}
        maxFixtures={BUILD_FIXTURE_COUNT}
        isCaptain={player.id === captainId}
        isViceCaptain={player.id === viceCaptainId}
        forceShowQuickActions
        onMakeCaptain={isStarter ? () => onMakeCaptain(player.id) : undefined}
        onMakeViceCaptain={
          isStarter ? () => onMakeViceCaptain(player.id) : undefined
        }
        onTransferOut={() => onRemovePlayer(player.id)}
      />
    );
  };

  const benchSlots: RenderSlot[] = [
    ...rowSlots(lineup, 'GK').filter((slot) => !slot.isStarter),
    ...rowSlots(lineup, 'DEF').filter((slot) => !slot.isStarter),
    ...rowSlots(lineup, 'MID').filter((slot) => !slot.isStarter),
    ...rowSlots(lineup, 'FWD').filter((slot) => !slot.isStarter)
  ];

  return (
    <div className="space-y-4" data-cy="build-lineup">
      <div
        className="relative w-full max-w-3xl mx-auto bg-no-repeat bg-cover bg-center rounded-lg overflow-hidden py-6 px-2"
        style={{ backgroundImage: "url('/images/pitches/pitch.png')" }}
        data-cy="build-lineup-pitch"
      >
        <div className="space-y-4">
          {POSITION_ORDER.map((position) => (
            <div
              key={position}
              className="flex justify-center gap-2 md:gap-3"
              data-cy={`build-lineup-row-${position.toLowerCase()}`}
            >
              {rowSlots(lineup, position)
                .filter((slot) => slot.isStarter)
                .map((slot) => (
                  <div
                    key={`${position}-${slot.location.index}`}
                    className="relative flex-1 min-w-0 max-w-[140px]"
                  >
                    {renderSlot(slot)}
                  </div>
                ))}
            </div>
          ))}
        </div>
      </div>

      <div className="m-auto flex max-w-[620px] justify-around py-3 bg-success-100 rounded-lg mt-2 md:mt-3">
        <div
          className="flex justify-around w-full gap-2"
          data-cy="build-lineup-bench"
        >
          {benchSlots.map((slot) => (
            <div
              key={`bench-${slot.location.position}-${slot.location.index}`}
              className="flex-1 min-w-0 max-w-[140px] px-px"
            >
              <div className="flex justify-center text-xs font-bold text-success-800 mb-1">
                {slot.location.position}
              </div>
              {renderSlot(slot)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BuildLineup;
